import { AttioTeamMemberResponse } from '../attio.types';
import { BaseAttioService } from './BaseAttioService';

// Service-specific types
export interface AttioConsultantData {
  name: string;
  imageSrc: string;
  email: string;
}

export class AttioConsultantService extends BaseAttioService {
  constructor() {
    super('AttioConsultantService');
  }
  /**
   * Get other consultant details for a valuation record
   */
  async getOtherConsultant(otherConsultantRecordId: string, signal?: AbortSignal): Promise<AttioConsultantData> {
    try {
      const response = await this.get<AttioTeamMemberResponse>(
        this.endpoints.retrieveTeamByRecordId(otherConsultantRecordId),
        signal
      );
      const responseData = response.data.values;
      const name = responseData.name?.[0]?.value || '-';
      const email = responseData.email?.[0]?.value || '-';
      const imageSrc = responseData.profile_image_url?.[0]?.value || '-';
      return {
        name,
        imageSrc,
        email,
      };
    } catch (error) {
      console.error('Error fetching other consultant:', error);
      return {
        name: '-',
        imageSrc: '-',
        email: '-',
      };
    }
  }
  /**
   * Search consultants (team members) by name
   */
  async searchConsultants(searchTerm: string): Promise<AttioConsultantData[]> {
    try {
      const body = {
        filter: {
          name: {
            $contains: searchTerm,
          },
        },
      };

      const response = await this.post<any>(this.endpoints.searchTeamMembers(), body);
      return response.data.map((member: any) => ({
        name: `${member.values.name?.[0]?.value || '-'}`,
        imageSrc: `${member.values.profile_image_url?.[0]?.value || '-'}`,
        email: `${member.values.email?.[0]?.value || '-'}`,
        targetRecordId: `${member.id.record_id}`,
      }));
    } catch (error) {
      console.warn('Error searching consultants:', error);
      return [];
    }
  }

  /**
   * Update valuation other_consultants field with an array of team target_record_ids
   */
  async updateValuationOtherConsultants({
    valuationId,
    otherConsultantTargetRecordIds,
  }: {
    valuationId: string;
    otherConsultantTargetRecordIds: string[];
  }): Promise<void> {
    const endpoint = this.endpoints.updateValuationByRecordId(valuationId);
    try {
      // Attio appends to relationship fields on PATCH — clear first, then set the full desired list.
      await this.patch(endpoint, { data: { values: { other_consultant: [] } } });
      await this.patch(endpoint, {
        data: {
          values: {
            other_consultant: otherConsultantTargetRecordIds.map(id => ({
              target_object: 'team',
              target_record_id: id,
            })),
          },
        },
      });
    } catch (error) {
      console.error('Error updating valuation other consultants:', error);
      throw error;
    }
  }
}
